import AsyncSelect from "react-select/async";
import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import PaginationComponent from "../../../components/Pagination";
import ErrorMessage from "../../../components/ErrorMessage";
import { bulkSMSAdminContext } from "../../../context/AdminContext/BulkSMSAdminContext/BulkSMSAdminContext";
import {
  createBulkSMSValidation,
  editBulkSMSValidation,
} from "../../../utils/validation/validation";
import { convertDate } from "../../../utils";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import {
  bulkSMSTypeItems,
  convertBulkSMSTypeToText,
  exporterData,
  smsToken,
} from "../../../utils/enum";
import { useSelector } from "react-redux";
import DataExporter from "../../../components/DataExporter";

const BulkSMSsAdmin = () => {
  const loader = useSelector((state) => state.loader);

  const {
    bulkSMSs,
    bulkSMS,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    type,
    setType,
    pageCount,
    pageNumber,
    setPageNumber,
    itemsCount,
    setItemsCount,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleGetBulkSMSs,
    handleCreateBulkSMS,
    handleEditBulkSMS,
    handleDeleteBulkSMS,
  } = useContext(bulkSMSAdminContext);

  useEffect(() => {
    handleGetBulkSMSs();
  }, [start, end, pageNumber, type, itemsCount, sort]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      isEmpty(bulkSMS) ? createBulkSMSValidation : editBulkSMSValidation
    ),
  });

  return (
    <>
      <SiteTitle title="پیامک های انبوه" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        bulkSMSType={type}
        setBulkSMSType={setType}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <div className="d-flex justify-content-between align-items-center gap-2">
              {!isEmpty(bulkSMSs) && (
                <DataExporter
                  headers={exporterData.bulkSMSs.headers}
                  keys={exporterData.bulkSMSs.keys}
                  data={bulkSMSs}
                  fileName={exporterData.bulkSMSs.fileName}
                />
              )}
              <button
                className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                onClick={() => {
                  handleShowModal();
                  reset();
                }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                افزودن پیامک انبوه
              </button>
            </div>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetBulkSMSs}
        />
        <div className="card-body p-24">
          {isEmpty(bulkSMSs) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">عنوان</th>
                      <th scope="col">نوع</th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {bulkSMSs.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>{item.title}</td>
                        <td>{convertBulkSMSTypeToText(item.type)}</td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() => {
                                  reset();
                                  handleShowModal(item, setValue);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                ویرایش
                              </li>
                              <li className="dropdown-divider" />
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteBulkSMS(item._id),
                                    `آیا از حذف پیامک انبوه ${item.title} مطمئنید؟`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(bulkSMS)
              ? "افزودن و ارسال پیامک انبوه جدید"
              : "ویرایش پیامک انبوه"}
          </span>
        </ModalHeader>
        <ModalBody>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(bulkSMS) ? handleCreateBulkSMS : handleEditBulkSMS
            )}
          >
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="title"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      عنوان
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="title"
                      placeholder="عنوان را وارد نمایید"
                      {...register("title")}
                    />
                  </div>

                  <ErrorMessage message={errors.title?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="type"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نوع
                    </label>
                    <select
                      disabled={!isEmpty(bulkSMS)}
                      className="form-control radius-8"
                      {...register("type")}
                    >
                      <option value="" selected hidden>
                        نوع را انتخاب نمایید
                      </option>
                      {bulkSMSTypeItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("type") === item}
                        >
                          {convertBulkSMSTypeToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.type?.message} />
                </div>
              </div>

              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="users"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      کاربران
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          isDisabled={
                            watch("type") !== bulkSMSTypeItems[1] ||
                            !isEmpty(bulkSMS)
                          }
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          isMulti
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا کاربران را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>کاربری برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetUsers(val, callback);
                          }}
                          onChange={(e) => {
                            onChange(e);
                          }}
                          value={value}
                          isClearable
                        />
                      )}
                      name="users"
                    />
                  </div>

                  <ErrorMessage message={errors.users?.message} />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="mb-20">
                  <div className="position-relative">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label
                        htmlFor="message"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        پیامک
                      </label>
                      {isEmpty(bulkSMS) && (
                        <div className="btn-group btn-group-sm" dir="ltr">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() =>
                              setValue(
                                "message",
                                `${watch("message")}${smsToken.address}`
                              )
                            }
                          >
                            آدرس
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() =>
                              setValue(
                                "message",
                                `${watch("message")}${smsToken.nationalNumber}`
                              )
                            }
                          >
                            کدملی
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() =>
                              setValue(
                                "message",
                                `${watch("message")}${smsToken.lastName}`
                              )
                            }
                          >
                            نام خانوادگی
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() =>
                              setValue(
                                "message",
                                `${watch("message")}${smsToken.firstName}`
                              )
                            }
                          >
                            نام
                          </button>
                        </div>
                      )}
                    </div>
                    <textarea
                      disabled={!isEmpty(bulkSMS)}
                      name="message"
                      className="form-control radius-8"
                      style={{
                        paddingBottom: 80,
                        minHeight: 300,
                      }}
                      id="message"
                      rows={5}
                      placeholder="پیامک ..."
                      {...register("message")}
                    />
                    <div
                      className="position-absolute text-muted p-3"
                      style={{ right: 5, bottom: 2 }}
                    >
                      نت بوف
                      <br />
                      لغو 11
                    </div>
                  </div>

                  <ErrorMessage message={errors.message?.message} />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="mb-20">
                  <label
                    htmlFor="description"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    توضیحات
                  </label>
                  <textarea
                    name="description"
                    className="form-control radius-8"
                    id="description"
                    rows={5}
                    placeholder="توضیحات ..."
                    {...register("description")}
                  />
                </div>
              </div>
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(bulkSMS) ? "افزودن و ارسال" : "ویرایش"} پیامک انبوه
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default BulkSMSsAdmin;
